import { computed, onMounted, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import Swal from "sweetalert2";

import reportService, { type ProductReportItem } from "@/services/report";
import warehouseService, { type Warehouse } from "@/services/warehouse";

type ProductReportFilters = {
  warehouse_id: number | null;
  per_page: number | "all";
};

const createInitialFilters = (): ProductReportFilters => ({
  warehouse_id: null,
  per_page: 10,
});

export function useProductReport() {
  const { t, locale } = useI18n();
  const products = ref<ProductReportItem[]>([]);
  const warehouses = ref<Warehouse[]>([]);
  const loading = ref(false);
  const exporting = ref(false);
  const searchQuery = ref("");
  const selectedProductIds = ref<number[]>([]);
  const currentPage = ref(1);
  const total = ref(0);
  const filters = reactive<ProductReportFilters>(createInitialFilters());
  const i18nScope = "product_report_page";
  const pageTitle = `${i18nScope}.title`;

  const pagination = computed(() => ({
    current: currentPage.value,
    pageSize: filters.per_page === "all" ? total.value || 10 : Number(filters.per_page),
    total: total.value,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "50", "100", "all"],
    showTotal: (total: number, range: [number, number]) =>
      t(`${i18nScope}.pagination_total`, { start: range[0], end: range[1], total }),
    locale: { items_per_page: t(`${i18nScope}.items_per_page`) },
  }));

  const queryParams = computed(() => ({
    page: currentPage.value,
    per_page: filters.per_page,
    search_query: searchQuery.value,
    warehouse_id: filters.warehouse_id || undefined,
  }));

  const summary = computed(() => ({
    totalSalesQty: products.value.reduce((sum, product) => sum + Number(product.Total_Ordered ?? 0), 0),
    totalRevenue: products.value.reduce((sum, product) => sum + Number(product.Revenue ?? 0), 0),
  }));

  const fetchWarehouses = async () => {
    try {
      warehouses.value = await warehouseService.getWarehouses();
    } catch (error) {
      console.error("Failed to fetch warehouses for product report:", error);
    }
  };

  const fetchProducts = async () => {
    loading.value = true;
    try {
      const response = await reportService.getProductReports(queryParams.value);
      products.value = response.data ?? [];
      currentPage.value = response.current_page;
      total.value = response.total;
      selectedProductIds.value = [];
    } catch (error) {
      console.error("Failed to fetch product reports:", error);
      Swal.fire({
        icon: "error",
        title: t(`${i18nScope}.load_failed`),
        text: t(`${i18nScope}.unable_to_load_report`),
      });
    } finally {
      loading.value = false;
    }
  };

  const handleTableChange = (page: number, pageSize: number | string) => {
    currentPage.value = Number(page);

    if (pageSize !== filters.per_page) {
      filters.per_page = pageSize === "all" ? "all" : Number(pageSize);
      currentPage.value = 1;
    }

    fetchProducts();
  };

  const updateSelectedProducts = (ids: number[]) => {
    selectedProductIds.value = ids;
  };

  const exportProducts = async () => {
    exporting.value = true;
    try {
      const response = await reportService.exportProductReports({
        warehouse_id: filters.warehouse_id || undefined,
        search_query: searchQuery.value,
        selected_products: selectedProductIds.value.length ? selectedProductIds.value : undefined,
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const contentDisposition = response.headers["content-disposition"];
      let fileName = `products_report_${new Date().getTime()}.csv`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) fileName = match[1];
      }
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to export product report:", error);
      Swal.fire({
        icon: "error",
        title: t(`${i18nScope}.export_failed`),
        text: t(`${i18nScope}.unable_to_export_report`),
      });
    } finally {
      exporting.value = false;
    }
  };

  watch(searchQuery, () => {
    currentPage.value = 1;
    fetchProducts();
  });

  watch(
    () => filters.warehouse_id,
    () => {
      currentPage.value = 1;
      fetchProducts();
    }
  );

  onMounted(async () => {
    await fetchWarehouses();
    await fetchProducts();
  });

  const formatReportAmount = (value?: string | number) =>
    Number(value ?? 0).toLocaleString(locale.value === "km" ? "km-KH" : "en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  return {
    pageTitle,
    i18nScope,
    products,
    warehouses,
    loading,
    exporting,
    searchQuery,
    selectedProductIds,
    filters,
    pagination,
    summary,
    formatReportAmount,
    handleTableChange,
    updateSelectedProducts,
    exportProducts,
  };
}
